import React from "react";
import { Box, Flex, Text, Divider, Button } from "@chakra-ui/react";
import { FaExclamationCircle } from "react-icons/fa";
import { ChevronRightIcon } from "@chakra-ui/icons";
import moment from "moment";
import { useNavigate } from "react-router-dom";

interface Notification {
  id?: string;
  referenceNumber: string | number;
  type: string;
  referenceId: string | number;
  description: string;
  dateCreated: string | number;
  isRead?: boolean;
}

interface NotificationListProps {
  notifications: Notification[];
  onItemClick: (item: Notification) => void;
  onDelete?: (item: Notification) => void;
  busyId?: string | null;
  maxHeight?: string;
  manageHref?: string;
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  onItemClick,
  onDelete,
  busyId,
  maxHeight = "400px",
  manageHref,
}) => {
  const navigate = useNavigate();
  return (
    <>
      <Box
        p="12px 15px"
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="gray.50"
      >
        <Flex justify="space-between" align="center">
          <Text fontWeight="700" fontSize="md" color="blue.400">
            Notifications ({notifications.length})
          </Text>
          {manageHref ? (
            <Button
              size="xs"
              variant="transparent"
              color="blue.400"
              _hover={{ color: "blue.400", textDecoration: "underline" }}
              onClick={() => navigate(manageHref)}
            >
              Manage
            </Button>
          ) : null}
        </Flex>
      </Box>
      <Box
        maxH={maxHeight === "none" ? undefined : maxHeight}
        overflowY={maxHeight === "none" ? "visible" : "auto"}
        css={{
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--clr1)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "var(--clr1)",
          },
        }}
      >
        {notifications.map((item, index) => (
          <Box key={item.id || `${item.referenceNumber}-${index}`}>
            <Flex
              align="start"
              justify="space-between"
              padding="12px 15px"
              cursor="pointer"
              transition="background 0.2s"
              _hover={{
                background: "blue.50",
              }}
            >
              <Flex
                align="start"
                flex="1"
                onClick={() => onItemClick(item)}
              >
                <Box mt="2px" mr="12px">
                  <FaExclamationCircle
                    fontSize="18px"
                    color={item.isRead === false ? "var(--clr1)" : "#A19F9D"}
                  />
                </Box>
                <Box flex="1">
                  <Text
                    fontSize="14px"
                    fontWeight={item.isRead === false ? "700" : "600"}
                    color="gray.800"
                    lineHeight="1.4"
                  >
                    {item.description}
                  </Text>
                  <Text
                    mt="4px"
                    fontSize="12px"
                    fontWeight="500"
                    color="gray.500"
                  >
                    {moment(item.dateCreated).format("Do MMMM, h:mm A")}
                  </Text>
                </Box>
              </Flex>
              {onDelete ? (
                <Button
                  size="xs"
                  variant="transparent"
                  color="#c2185c"
                  _hover={{ color: "#8c1144", textDecoration: "underline" }}
                  isLoading={busyId === (item.id || item.referenceNumber)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }}
                >
                  Delete
                </Button>
              ) : (
                <Box
                  pl="8px"
                  alignSelf="center"
                  onClick={() => onItemClick(item)}
                >
                  <ChevronRightIcon fontSize="20px" color="gray.400" />
                </Box>
              )}
            </Flex>
            <Divider borderColor="gray.50" />
          </Box>
        ))}
      </Box>
    </>
  );
};

export default NotificationList;
